import { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import CircularProgress from '@mui/material/CircularProgress';
import { styled, alpha } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { useCity } from '../contexts/CityContext';
import { usePopularCities } from '../features/locations/hooks/useLocations';
import type { CityResponse } from '@lov/shared';

const SwitcherButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 10px',
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  color: theme.palette.text.primary,
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.8125rem',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  maxWidth: 160,
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    borderColor: theme.palette.text.secondary,
  },
}));

const CityLabel = styled(Typography)({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.8125rem',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 90,
});

const PopoverContainer = styled(Box)(({ theme }) => ({
  width: 320,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const SearchBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  marginBottom: theme.spacing(2),
}));

const CityGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(1),
  maxHeight: 280,
  overflowY: 'auto',
}));

const CityChip = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${isSelected ? theme.palette.success.main : theme.palette.divider}`,
  backgroundColor: isSelected ? alpha(theme.palette.success.main, 0.1) : 'transparent',
  color: isSelected ? theme.palette.success.dark : theme.palette.text.primary,
  cursor: 'pointer',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.8125rem',
  fontWeight: isSelected ? 600 : 400,
  textAlign: 'left',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isSelected
      ? alpha(theme.palette.success.main, 0.15)
      : alpha(theme.palette.text.primary, 0.04),
    borderColor: theme.palette.success.main,
  },
}));

export function CitySwitcher() {
  const { selectedCity, setCity, isDetectingCity } = useCity();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: popularCities, isLoading } = usePopularCities();

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setSearch('');
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleSelectCity = (city: CityResponse) => {
    setCity({ id: city.id, name: city.name, slug: city.slug });
    handleClose();
  };

  const filteredCities = (popularCities || []).filter((city) =>
    city.name.toLowerCase().includes(search.toLowerCase())
  );

  const buttonLabel = isDetectingCity
    ? 'Detecting...'
    : selectedCity
      ? selectedCity.name
      : 'Select City';

  return (
    <>
      <SwitcherButton ref={buttonRef} onClick={handleOpen} type="button">
        {isDetectingCity ? (
          <CircularProgress size={12} sx={{ flexShrink: 0 }} />
        ) : (
          <LocationOnIcon sx={{ fontSize: 14, flexShrink: 0, color: 'success.main' }} />
        )}
        <CityLabel>{buttonLabel}</CityLabel>
        <KeyboardArrowDownIcon sx={{ fontSize: 16, flexShrink: 0, color: 'text.secondary' }} />
      </SwitcherButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: { borderRadius: 2, mt: 0.5 },
          },
        }}
      >
        <PopoverContainer>
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontWeight: 600,
              color: 'text.secondary',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 1.5,
            }}
          >
            Select City
          </Typography>

          {/* Search */}
          <SearchBox>
            <SearchIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cities..."
              fullWidth
              sx={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}
            />
          </SearchBox>

          {/* City grid */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <CityGrid>
              {filteredCities.map((city) => (
                <CityChip
                  key={city.id}
                  isSelected={selectedCity?.id === city.id}
                  onClick={() => handleSelectCity(city)}
                  type="button"
                >
                  {city.name}
                </CityChip>
              ))}
              {filteredCities.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    gridColumn: 'span 2',
                    textAlign: 'center',
                    py: 2,
                  }}
                >
                  No cities found
                </Typography>
              )}
            </CityGrid>
          )}
        </PopoverContainer>
      </Popover>
    </>
  );
}
